// Shared DOM helpers and selector constants for passive FullStory tracking.
import { normalizeText } from 'app/shared/utils/text/text-helpers';

export class PassiveTrackingDom {
  static readonly FIELD_SELECTOR = 'input, select, textarea, calendar-date';
  static readonly WIDGET_INTERNAL_HOST_SELECTORS = ['calendar-date', 'kin-address-autocomplete'];
  static readonly BUTTON_INPUT_SELECTOR = 'input[type="submit"], input[type="button"]';
  static readonly RADIO_INPUT_SELECTOR = 'input[type="radio"]';
  static readonly RADIO_CARD_SELECTOR = 'label:has(input[type="radio"])';

  static getElementLabel(element: HTMLElement): string {
    if (element instanceof HTMLInputElement) {
      return element.value || element.getAttribute('aria-label') || element.name || element.id;
    }

    return element.getAttribute('aria-label') || element.getAttribute('title') || element.textContent || element.id || element.getAttribute('data-testid') || '';
  }

  static getLabelText(labelLikeElement: Element | null): string | null {
    if (!(labelLikeElement instanceof HTMLElement)) return null;

    const cloned = labelLikeElement.cloneNode(true);
    if (!(cloned instanceof HTMLElement)) return null;

    cloned.querySelectorAll('input, select, textarea, calendar-date, ng-icon, svg').forEach((node) => node.remove());

    return normalizeText(cloned.textContent);
  }

  /** Pulls the bold "title" text from a radio card label (the option name shown to the user). */
  static getLabelTitleText(labelLikeElement: Element | null): string | null {
    if (!(labelLikeElement instanceof HTMLElement)) return null;

    const titleElement = labelLikeElement.querySelector('.radio-card__title');
    if (!(titleElement instanceof HTMLElement)) return null;

    return normalizeText(titleElement.textContent);
  }

  /**
   * Finds the visible label text for a form field. Looks in this order:
   * a wrapping <label>, then a <label for="..."> match, then the nearest <legend>.
   */
  static getAssociatedLabelText(element: HTMLElement): string | null {
    const wrappedLabel = element.closest('label');
    const radioOptionTitle = PassiveTrackingDom.getLabelTitleText(wrappedLabel);
    if (radioOptionTitle) return radioOptionTitle;

    const wrappedLabelText = PassiveTrackingDom.getLabelText(wrappedLabel);
    if (wrappedLabelText) return wrappedLabelText;

    const elementId = element.getAttribute('id');
    if (elementId) {
      const doc = element.ownerDocument;
      const cssEscape = doc?.defaultView?.CSS?.escape;
      const escapedElementId = cssEscape ? cssEscape(elementId) : elementId;
      const explicitLabel = doc?.querySelector(`label[for="${escapedElementId}"]`) ?? null;
      const explicitLabelText = PassiveTrackingDom.getLabelText(explicitLabel);
      if (explicitLabelText) return explicitLabelText;
    }

    const fieldset = element.closest('fieldset');
    const legendText = PassiveTrackingDom.getLabelText(fieldset?.querySelector('legend') || null);
    if (legendText) return legendText;

    return null;
  }

  /**
   * True when the click/focus path goes through a custom widget (calendar, address
   * autocomplete) whose internal clicks and fields we don't want to track separately.
   */
  static pathIncludesIgnoredWidget(path: HTMLElement[]): boolean {
    return path.some((element) => {
      const tagName = element.tagName.toLowerCase();
      if (PassiveTrackingDom.WIDGET_INTERNAL_HOST_SELECTORS.includes(tagName)) return true;

      return PassiveTrackingDom.WIDGET_INTERNAL_HOST_SELECTORS.some((selector) => !!element.closest(selector));
    });
  }

  /**
   * Treats an <a> as button-like when it's an internal link, uses the "button" CSS class,
   * or is a tel: link. External content links are skipped.
   */
  static isTrackableAnchor(element: HTMLElement, origin: string): boolean {
    const href = (element.getAttribute('href') || '').trim();
    const className = element.className || '';
    const hasButtonClass = /\bbutton\b/.test(className);
    const isInternalLink = href.startsWith('/') || (!!origin && href.startsWith(origin));

    return hasButtonClass || isInternalLink || /^tel:/i.test(href);
  }
}
